<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router/composables'
import { useInfiniteScroll } from '@vueuse/core'
import { searchConfig as rocnovelConfig } from './configs/rocnovel'
import { searchConfig as ocereadConfig } from './configs/oceread'
import { searchConfig as talewaveConfig } from './configs/talewave'
import { searchConfig as ficmoonConfig } from './configs/ficmoon'
import { searchConfig as novelarcConfig } from './configs/novelarc'
import { searchConfig as storyellaConfig } from './configs/storyella'
import { searchConfig as florastoryConfig } from './configs/florastory'
import { searchConfig as silktaleConfig } from './configs/silktale'
import { searchConfig as pearlaConfig } from './configs/pearla'
import { searchConfig as fictoraConfig } from './configs/fictora'
import { searchConfig as marinovelConfig } from './configs/marinovel'
import { searchConfig as flipnovelConfig } from './configs/flipnovel'
import { searchConfig as moonpageConfig } from './configs/moonpage'
import { searchConfig as storlynConfig } from './configs/storlyn'
import { searchConfig as novarisConfig } from './configs/novaris'
import SearchPageDefault from './components/SearchPage.default.vue'
import SearchPageFlipnovel from './components/SearchPage.flipnovel.vue'
import { api } from '@/api'
import { useHistoryStore, useQueryStore, useToastStore, useTrackValueStore } from '@/stores'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { TRACK_CHANNEL_NAME_VALUE } from '@/constants'
import { searchTrack } from '@/composables/tracks'
import { loadBrandComponent, loadBrandConfig } from '@/utils/loadBrandConfig'

// 🔥 加载当前品牌配置
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  talewave: talewaveConfig,
  ficmoon: ficmoonConfig,
  novelarc: novelarcConfig,
  storyella: storyellaConfig,
  florastory: florastoryConfig,
  silktale: silktaleConfig,
  pearla: pearlaConfig,
  fictora: fictoraConfig,
  marinovel: marinovelConfig,
  flipnovel: flipnovelConfig,
  moonpage: moonpageConfig,
  storlyn: storlynConfig,
  novaris: novarisConfig,
})

const SearchPage = loadBrandComponent({
  rocnovel: SearchPageDefault,
  oceread: SearchPageDefault,
  talewave: SearchPageDefault,
  ficmoon: SearchPageDefault,
  novelarc: SearchPageDefault,
  storyella: SearchPageDefault,
  florastory: SearchPageDefault,
  silktale: SearchPageDefault,
  pearla: SearchPageDefault,
  fictora: SearchPageDefault,
  marinovel: SearchPageDefault,
  flipnovel: SearchPageFlipnovel,
  storlyn: SearchPageFlipnovel,
  novaris: SearchPageFlipnovel,
})

const { $toast } = useToastStore()
const input = ref('')
function onClear() {
  input.value = ''
}

const enterSearchTime = ref<number>(Date.now())

const route = useRoute()
const router = useRouter()
onMounted(() => {
  if (route.query.kw) {
    input.value = route.query.kw as string
    onSearch()
  }

  if (route.query.st)
    enterSearchTime.value = Number(route.query.st)
})

const skeleton = ref(false)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const page = ref(1)
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))

const passcodePID = ref('')
const passcode = ref<any>()
const result = ref<any[]>([])

async function onSearch() {
  searchTrack({
    input: input.value,
    currentPage: route.name as string,
    currentPageOpenTime: String(enterSearchTime.value),
    resultPageOpenTime: String(Date.now()),
    duration: String(Date.now() - enterSearchTime.value),
  })

  enterSearchTime.value = Date.now()
  skeleton.value = true
  ending.value = false
  error.value = null
  page.value = 1
  result.value = []
  fetchResult()
}

async function fetchResult() {
  fetching.value = true
  try {
    const { data } = await api.get<any, GetContentGetcontentbypassornameResponse>('/content/getContentByPassOrName', { params: { passOrName: input.value, ...pagination.value } })
    passcode.value = data.codeSearchResult
    passcodePID.value = data.promotionId ?? ''

    if (!data.nameSearchResult) {
      ending.value = true
      return
    }

    result.value = [...result.value, ...(data.nameSearchResult.records ?? [])]
    if (page.value >= data.nameSearchResult.pages)
      ending.value = true
    else
      page.value += 1
  }
  catch (_error: any) {
    console.error(_error)
    error.value = _error
    $toast(_error.msg || '服务器错误')
  }
  finally {
    fetching.value = false
    skeleton.value = false
  }
}

const infiniteRef = ref<HTMLElement>()
function setInfiniteRef(el: HTMLElement | null) {
  infiniteRef.value = el || undefined
}
useInfiniteScroll(infiniteRef,
  async () => {
    if (fetching.value || ending.value || error.value)
      return
    await fetchResult()
  },
  { distance: 50 },
)

const queryStore = useQueryStore()
const historyStore = useHistoryStore()
async function gotoPasscodeBook(passcode: any) {
  if (passcodePID.value) // Note: 如果存在 promotionId, 需要更新全局 `max_pid`
    queryStore.$state.max_pid = passcodePID.value

  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId: passcode.id, firstOrigin: 3, originName: 3, chlName: TRACK_CHANNEL_NAME_VALUE.SEARCH_LIST })
  // Note: clear custom history stack, avoid back to old `max_pid`
  historyStore.$clear()
  router.replace({ name: READER_ROUTE_NAME, params: { contentId: passcode.id, chapterId: passcode.firstChapter?.id || '' } })
}

function onTrackGotoBook(bookId: string) {
  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId, firstOrigin: 3, originName: 3, chlName: TRACK_CHANNEL_NAME_VALUE.SEARCH_LIST })
  return true
}

const gotoBack = function () {
  historyStore.$clear()
  router.replace({ name: 'home' })
}

onBeforeRouteLeave((to, from, next) => {
  searchTrack({
    input: input.value,
    currentPage: to.path as string,
    currentPageOpenTime: String(enterSearchTime.value),
    resultPageOpenTime: String(Date.now()),
    duration: String(Date.now() - enterSearchTime.value),
  })
  next()
})
</script>

<template>
  <SearchPage
    :config="config"
    :input="input"
    :skeleton="skeleton"
    :fetching="fetching"
    :ending="ending"
    :error="error"
    :passcode="passcode"
    :result="result"
    :set-infinite-ref="setInfiniteRef"
    :on-clear="onClear"
    :on-search="onSearch"
    :goto-back="gotoBack"
    :goto-passcode-book="gotoPasscodeBook"
    :on-track-goto-book="onTrackGotoBook"
    @update:input="input = $event"
  />
</template>
